/**
 * MondayConnectedPage — where the "Connect monday.com" popup lands.
 *
 * It says how the connection ended and closes itself. The White-Label page
 * that opened it does not rely on a message from here: it asks the server
 * whether the organization is connected now (the popup may have lost its
 * opener on monday's pages). Tenant-less and session-less on purpose
 * (routing/popupLandings.ts).
 */

import { useEffect, useMemo, useState } from 'react';
import { ApiOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import '@/modules/cloud-storage/components/GoogleAccessPrompt.css';

const STATUSES = ['connected', 'declined', 'error'] as const;
type Status = typeof STATUSES[number];

export function MondayConnectedPage() {
  const { t } = useLanguage();
  const status = useMemo<Status>(() => {
    const raw = new URLSearchParams(window.location.search).get('status');
    return (STATUSES as readonly string[]).includes(raw ?? '') ? raw as Status : 'error';
  }, []);
  const [stillOpen, setStillOpen] = useState(false);

  useEffect(() => {
    const close = window.setTimeout(() => {
      window.close();
      // Still here: the browser would not let this window close itself.
      window.setTimeout(() => setStillOpen(true), 300);
    }, 1200);
    return () => window.clearTimeout(close);
  }, []);

  return (
    <main className="google-access google-access--panel google-access--page" data-testid="monday-connected">
      <span className="google-access__logo" aria-hidden="true"><ApiOutlined /></span>
      <h1 className="google-access__title">{t(`whiteLabel.supportBoard.monday.landing.${status}`)}</h1>
      {stillOpen && <p className="google-access__body">{t('whiteLabel.supportBoard.monday.landing.close')}</p>}
    </main>
  );
}
